import React, { useState, useRef, useCallback, Fragment } from 'react';
import CobaltDropdown, { Content } from '@cobalt/react-dropdown';
import useOutsideClick from '@cobalt/react-outside-click-hook';
import { Input, DropdownError, OptionsWrapper, GenericOption } from '@cobalt-marketplace/dropdown-utils';
import Box from '@cobalt/react-box';
import Divider from '@cobalt/react-divider';
import Icon from '@cobalt/react-icon';
import { ClickableItem } from '@cobalt/react-list';
import { useTheme } from '@cobalt/react-theme-provider';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const defaultMessages = {
  placeholder: "Select an option"
};

const Dropdown = ({
  children,
  contentSize,
  customInput,
  defaultOpen = false,
  disabled = false,
  dismissible = false,
  error = false,
  inputSize,
  loading = false,
  messages,
  onBlur,
  onChange,
  onClose,
  searchable = false,
  value,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const inputRef = useRef(null);
  const handlers = useOutsideClick(() => {
    setIsOpen(false);
    onClose === null || onClose === void 0 ? void 0 : onClose();
  }, isOpen); // Avoid having elements without translations

  const translations = { ...defaultMessages,
    ...messages
  };
  const onClickInput = useCallback(event => {
    // In some projects there are problems where the options never show because the event
    // propagates to the outside click hook. This is to prevent that from happening
    event.stopPropagation();
    event.preventDefault();

    if (isOpen) {
      onClose === null || onClose === void 0 ? void 0 : onClose();
    }

    setIsOpen(preIsOpen => !preIsOpen);
  }, [isOpen, onClose]);
  const onClear = useCallback(event => {
    event.stopPropagation(); // There is a warning here because we are passing two properties but the extended
    // type could have more. We will assume that these are the only required and the
    // developer should assume that, when cleared, he won't have the other available
    // @ts-ignore

    onChange({
      value: null,
      title: ""
    });
  }, [onChange]);
  const onClickOption = useCallback(option => {
    setIsOpen(false);
    onClose === null || onClose === void 0 ? void 0 : onClose();
    onChange(option);
  }, [onChange, onClose]);
  const ContentComponent = searchable ? Fragment : OptionsWrapper;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CobaltDropdown, _extends({
    open: isOpen
  }, props), customInput ? /*#__PURE__*/React.cloneElement(customInput, {
    // If it is a native HTML element we pass the ref prop. If not, we use the standard
    // in cobalt components
    [typeof (customInput === null || customInput === void 0 ? void 0 : customInput.type) === "function" ? "forwardedRef" : "ref"]: inputRef,
    onClick: onClickInput
  }) : /*#__PURE__*/React.createElement(Input, {
    disabled: disabled,
    dismissible: value ? dismissible : false,
    error: error,
    forwardedRef: inputRef,
    loading: loading,
    onBlur: onBlur,
    onClear: onClear,
    onClick: onClickInput,
    open: isOpen,
    placeholder: translations.placeholder,
    size: inputSize
  }, value), children && /*#__PURE__*/React.createElement(Content, _extends({
    inputRef: inputRef.current,
    size: contentSize
  }, handlers), /*#__PURE__*/React.createElement(ContentComponent, null, typeof children === "function" ? children({
    onClick: onClickOption,
    setIsOpen
  }) : React.Children.map(children, child => {
    if ( /*#__PURE__*/React.isValidElement(child)) {
      var _child$props$onClick;

      return /*#__PURE__*/React.cloneElement(child, {
        // Try to use first the component onClick. If does not exist, use the
        // one by default
        onClick: (_child$props$onClick = child.props.onClick) !== null && _child$props$onClick !== void 0 ? _child$props$onClick : onClickOption
      });
    }

    return child;
  })))), error && translations.error && /*#__PURE__*/React.createElement(DropdownError, null, translations.error));
};

const Option = ({
  borderBottom = false,
  description,
  disabled = false,
  icon,
  selected = false,
  onClick,
  title,
  value,
  ...props
}) => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClickableItem, _extends({
    "data-option-title": title,
    "data-option-value": value,
    disabled: disabled,
    onClick: () => onClick && onClick({
      title,
      value
    }),
    selected: selected,
    style: {
      marginLeft: theme.baseSpacing2,
      marginRight: theme.baseSpacing2
    }
  }, props), /*#__PURE__*/React.createElement(GenericOption, {
    description: description,
    title: title
  }, !icon ? /*#__PURE__*/React.createElement(Icon, {
    name: "check",
    size: "tiny",
    style: {
      visibility: selected ? "visible" : "hidden"
    }
  }) : typeof icon === "string" ? /*#__PURE__*/React.createElement(Icon, {
    name: icon,
    size: "tiny"
  }) : icon)), borderBottom && /*#__PURE__*/React.createElement(Box, {
    paddingBottom: "1"
  }, /*#__PURE__*/React.createElement(Divider, null)));
};

export default Dropdown;
export { Option };
